export type AnthropicUsage = {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens?: number;
  cache_read_input_tokens?: number;
};

// $/M tokens (input, output). Rates are public list prices; cached input
// follows Anthropic's caching multipliers below.
export const ANTHROPIC_PRICING: Record<string, { input: number; output: number }> = {
  'claude-opus-4-7': { input: 15.0, output: 75.0 },
  'claude-opus-4-6': { input: 15.0, output: 75.0 },
  'claude-sonnet-4-6': { input: 3.0, output: 15.0 },
  'claude-haiku-4-5': { input: 1.0, output: 5.0 },
};

const ANTHROPIC_FALLBACK = { input: 3.0, output: 15.0 };
const CACHE_WRITE_MULT = 1.25;
const CACHE_READ_MULT = 0.1;

export function anthropicPricingFor(model: string) {
  if (ANTHROPIC_PRICING[model]) return ANTHROPIC_PRICING[model];
  // Strip context-tier suffix like "[1m]" if present.
  const base = model.replace(/\[.*\]$/, '');
  if (ANTHROPIC_PRICING[base]) return ANTHROPIC_PRICING[base];
  for (const k of Object.keys(ANTHROPIC_PRICING)) {
    if (model.startsWith(k) || base.startsWith(k)) return ANTHROPIC_PRICING[k];
  }
  return ANTHROPIC_FALLBACK;
}

export function anthropicCost(usage: AnthropicUsage, model: string): number {
  const p = anthropicPricingFor(model);
  const per = (n: number, rate: number) => (n / 1_000_000) * rate;
  return (
    per(usage.input_tokens, p.input) +
    per(usage.output_tokens, p.output) +
    per(usage.cache_creation_input_tokens ?? 0, p.input * CACHE_WRITE_MULT) +
    per(usage.cache_read_input_tokens ?? 0, p.input * CACHE_READ_MULT)
  );
}

// $/character. ElevenLabs is plan-based so these are rough Creator-tier estimates.
export const ELEVEN_PRICING: Record<string, number> = {
  eleven_v3: 0.0003,
  eleven_multilingual_v2: 0.0003,
  eleven_turbo_v2_5: 0.00015,
  eleven_flash_v2_5: 0.000075,
};

const ELEVEN_FALLBACK = 0.0003;

export function elevenPricingFor(model: string): number {
  return ELEVEN_PRICING[model] ?? ELEVEN_FALLBACK;
}

export function elevenCost(chars: number, model: string): number {
  return chars * elevenPricingFor(model);
}

export function fmtUsd(n: number): string {
  if (n === 0) return '$0.00';
  if (n < 0.01) return '<$0.01';
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function fmtNum(n: number): string {
  return n.toLocaleString();
}
